import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { FormattedCurrencyAmount, useDateFormatter } from '@swf/poker';

import LaurelIcon from '../../../../assets/icon-laurel.svg';
import RibbonIcon from '../../../../assets/icon-ribbon.svg';
import { usePokerSpinAndGoWidget } from '../../../../model';
import type { FeaturedTournament } from '../../../../types/WatchGames';
import { multiplierHelper } from '../../../../utils/numbers';
import Avatar from '../Avatar/Avatar';

import {
  TXTCLI_Hall_Of_Fame,
  TXTCLI_Something_went_wrongX_please_t_ELL
} from './HallOfFame.messages.json';
import styles from './HallOfFame.scss';

interface HallOfFameProps {
  games: FeaturedTournament[];
}

interface HallOfFameWinnerProps {
  game: FeaturedTournament;
}

interface SearchParams {
  widgetId: string;
}

const HallOfFameWinner: React.FC<HallOfFameWinnerProps> = ({ game }) => {
  const { winner, prizePool, tournEndTime, imageId, currency, winAmount, buyIn } = game;
  const { widgetId } = useSearchParams<SearchParams>();
  const { widgetSelect } = usePokerSpinAndGoWidget(widgetId);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const userImage = useSelector((state: RootState) => widgetSelect.userImage(state, imageId));

  const multiplier = multiplierHelper({
    buyIn,
    prizePool,
    isPlayMoney
  });

  const date = useDateFormatter({
    timestamp: tournEndTime * 1000
  });

  return (
    <li className={styles.winnerItem}>
      <div className={styles.winnerUser}>{winner}</div>
      <Avatar avatar={userImage} userName={winner} />
      <div className={styles.gamePrize}>
        <RibbonIcon />
        <div className={styles.value}>
          <FormattedCurrencyAmount
            amount={winAmount}
            isPlayMoney={isPlayMoney}
            currency={currency}
            notationPM="compact"
            currencyDisplay="narrowSymbol"
          />
        </div>
        <div className={styles.multiplier}>x{multiplier}</div>
      </div>
      <time className={styles.date}>{date}</time>
    </li>
  );
};

const HallOfFame: React.FC<HallOfFameProps> = ({ games }) => (
  <div className={styles.hallOfFameTab}>
    <h3>
      <LaurelIcon />
      <Translation {...TXTCLI_Hall_Of_Fame} />
      <LaurelIcon />
    </h3>
    <ul className={styles.hallOfFameList}>
      {games.length ? (
        games.map((game) => <HallOfFameWinner game={game} key={game.tournId} />)
      ) : (
        <div data-testid="empty-container" className={styles.empty}>
          <Translation {...TXTCLI_Something_went_wrongX_please_t_ELL} />
        </div>
      )}
    </ul>
  </div>
);

export default HallOfFame;
